var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import Observable from "zen-observable";
import client from "@irati/utils/services/apollo";
import { TeamState } from "@irati/types";
import listFormat from "@irati/utils/services/graph/utils/listFormat";
import PBService from "@irati/utils/services";
import teamErrorHandler from "./errors/legacy/teamErrorHandler";
import listTeamsQuery from "./queries/getTeams";
import getTeamQuery, { teamKey } from "./queries/getTeam";
import addUserToTeamMutation from "./mutations/addUserToTeam";
import removeUserFromTeamMutation from "./mutations/removeUserFromTeam";
import createTeamMutation from "./mutations/createTeam";
import updateTeamMutation from "./mutations/updateTeam";
import deleteTeamMutation from "./mutations/deleteTeam";
import fullTeamV4 from "./fragments/fullTeamV4";
var TeamService = /** @class */ (function (_super) {
    __extends(TeamService, _super);
    function TeamService() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    TeamService.prototype.create = function (_a, options) {
        var tenantId = _a.tenantId, data = _a.data;
        var variables = {
            tenantId: tenantId,
            data: teamInput(data)
        };
        return client.mutate(__assign(__assign({}, options), { mutation: createTeamMutation, variables: variables })).then(function (res) {
            var createdTeam = res.data.createTeam;
            addTeamToCache(createdTeam, { tenantId: tenantId, state: TeamState.Ok, userTotalOnly: true });
            addTeamToCache(createdTeam, { tenantId: tenantId, state: TeamState.Ok, userTotalOnly: false });
            return createdTeam;
        });
    };
    TeamService.prototype.get = function (_a, options) {
        var tenantId = _a.tenantId, teamId = _a.teamId;
        var variables = {
            tenantId: tenantId,
            teamId: teamId
        };
        return client.query(__assign(__assign({}, options), { query: getTeamQuery, variables: variables })).then(function (_a) {
            var data = _a.data;
            return data[teamKey];
        });
    };
    TeamService.prototype.update = function (tenantId, team, options) {
        var variables = {
            tenantId: tenantId,
            data: teamUpdateInput(team)
        };
        return client
            .mutate(__assign(__assign({}, options), { mutation: updateTeamMutation, variables: variables }))
            .then(function (_a) {
            var data = _a.data;
            return data.updateTeam;
        });
    };
    TeamService.prototype.delete = function (tenantId, teamId, options) {
        var variables = {
            tenantId: tenantId,
            teamId: teamId
        };
        return client.mutate(__assign(__assign({}, options), { mutation: deleteTeamMutation, variables: variables })).then(function () {
            /**
             * @description: On delete we want to remove team from lists on the UI. Setting teamState to "Deleted" and filtering on watchList.
             *
             * This should be replaced with a simple client.cache.evict(`Team@${teamId}`) when we update to Apollo V3.
             */
            var currentData = client.readFragment({ id: "Team@" + teamId, fragment: fullTeamV4, fragmentName: "fullTeam" });
            client.writeFragment({
                id: "Team@" + teamId,
                fragment: fullTeamV4,
                fragmentName: "fullTeam",
                data: __assign(__assign({}, currentData), { teamState: TeamState.Deleted })
            });
            return true;
        });
    };
    TeamService.prototype.list = function (tenantId, _a, options) {
        var _b = _a === void 0 ? {} : _a, _c = _b.state, state = _c === void 0 ? TeamState.Ok : _c, _d = _b.userTotalOnly, userTotalOnly = _d === void 0 ? true : _d;
        var variables = {
            tenantId: tenantId,
            state: state,
            userTotalOnly: userTotalOnly
        };
        return client
            .query(__assign(__assign({}, options), { query: listTeamsQuery, variables: variables }))
            .then(function (_a) {
            var data = _a.data;
            return listFormat(data.teams);
        });
    };
    TeamService.prototype.watchList = function (tenantId, _a, options) {
        var _b = _a === void 0 ? {} : _a, _c = _b.state, state = _c === void 0 ? TeamState.Ok : _c, _d = _b.userTotalOnly, userTotalOnly = _d === void 0 ? true : _d;
        var variables = {
            tenantId: tenantId,
            state: state,
            userTotalOnly: userTotalOnly
        };
        /** @note See delete function notes regarding Apollo V3. Change to simple return `watchQuery` function. */
        return new Observable(function (observer) {
            return client.watchQuery(__assign(__assign({}, options), { query: listTeamsQuery, variables: variables })).subscribe(function (data) {
                data.data.teams.results = data.data.teams.results.filter(function (t) { return t.teamState !== TeamState.Deleted; });
                observer.next(data);
            });
        });
    };
    TeamService.prototype.addUserToTeam = function (teamId, userId, options) {
        var variables = {
            teamId: teamId,
            userId: userId
        };
        return client
            .mutate(__assign(__assign({}, options), { mutation: addUserToTeamMutation, variables: variables }))
            .then(function (_a) {
            var data = _a.data;
            return data.addUserToTeam;
        });
    };
    TeamService.prototype.removeUserFromTeam = function (teamId, userId, options) {
        var variables = {
            teamId: teamId,
            userId: userId
        };
        return client
            .mutate(__assign(__assign({}, options), { mutation: removeUserFromTeamMutation, variables: variables }))
            .then(function (_a) {
            var data = _a.data;
            return data.removeUserFromTeam;
        });
    };
    return TeamService;
}(PBService));
export default new TeamService({ errorHandlers: [teamErrorHandler] });
function teamInput(team) {
    var data = {
        public_name: team.public_name,
        permissions: team.permissions,
        owners: team.owners,
        state: team.state
    };
    return data;
}
function teamUpdateInput(team) {
    var data = {
        uuid: team.uuid,
        public_name: team.public_name,
        state: team.state
    };
    return data;
}
function addTeamToCache(createdTeam, variables) {
    try {
        var cachedData = client.readQuery({ query: listTeamsQuery, variables: variables });
        if (cachedData === null || cachedData === void 0 ? void 0 : cachedData.teams) {
            var results = __spreadArrays([createdTeam], cachedData.teams.results);
            client.writeQuery({
                query: listTeamsQuery,
                variables: variables,
                data: {
                    teams: __assign(__assign({}, cachedData.teams), { total: results.length, results: results })
                }
            });
        }
    }
    catch (e) { /* no-op */ }
}
